import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Banner,
  Button,
  Card,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  TextField,
} from '@shopify/polaris';
import { createApiClient, ApiError, type AdminSession } from './api/client';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AdminShell } from './layout/AdminShell';
import { OverviewPage } from './pages/OverviewPage';
import { SyncPage } from './pages/SyncPage';
import { CatalogPage } from './pages/CatalogPage';
import { OrdersPaymentsPage } from './pages/OrdersPaymentsPage';
import { NovaPoshtaOrdersPage } from './pages/NovaPoshtaOrdersPage';
import { SettingsPage } from './pages/SettingsPage';
import { TelegramSettingsPage } from './pages/TelegramSettingsPage';
import { NovaPoshtaSettingsPage } from './pages/NovaPoshtaSettingsPage';
import { LogsPage } from './pages/LogsPage';
import { ActionBanner } from './components/ActionBanner';
import type { AdminToast } from './components/AdminToastHost';
import { TelegramAccessDenied } from './components/TelegramAccessDenied';
import { PakoLogo } from './components/PakoLogo';
import {
  closeTelegramMiniApp,
  getTelegramInitData,
  prepareTelegramMiniApp,
  resolveTelegramLaunchState,
  waitForTelegramMiniApp,
} from './lib/telegramMiniApp';
import { getOverviewPollIntervalMs, presentTopbarEnvironment } from './lib/adminUiState';
import { ADMIN_ROUTE_SET } from './lib/adminRoutes';

type ApiClient = ReturnType<typeof createApiClient>;

let telegramAuthBootstrap: null | {
  initData: string;
  promise: Promise<AdminSession>;
} = null;

function authenticateTelegramWebappOnce(api: ApiClient, initData: string) {
  if (telegramAuthBootstrap?.initData === initData) {
    return telegramAuthBootstrap.promise;
  }

  const promise = api.telegramWebappAuth(initData).finally(() => {
    if (telegramAuthBootstrap?.initData === initData) {
      telegramAuthBootstrap = null;
    }
  });
  telegramAuthBootstrap = {
    initData,
    promise,
  };
  return promise;
}

function resolvePathname(pathname: string) {
  if (pathname === '/' || pathname === '') {
    return '/overview';
  }

  return ADMIN_ROUTE_SET.has(pathname) ? pathname : '/overview';
}

function buildGlobalBanners(overview: any) {
  if (!overview) return [];

  const banners = [];
  if (overview.modeSummary?.payment?.mode === 'sandbox' || overview.environment?.liqpayMode === 'sandbox') {
    banners.push(
      <ActionBanner key="sandbox" tone="warning" title="LiqPay у тестовому режимі">
        Оплати працюють у тестовому режимі. Реальні списання не виконуються.
      </ActionBanner>,
    );
  }

  if ((overview.alerts || []).includes('onec_worker_unreachable')) {
    banners.push(
      <ActionBanner key="onec-worker" tone="critical" title="Сервер синхронізації недоступний">
        Адмінка не може дістатися сервера синхронізації. Автоматичні перевірки і ручні дії зараз ненадійні.
      </ActionBanner>,
    );
  }

  if ((overview.alerts || []).includes('pay_web_unreachable')) {
    banners.push(
      <ActionBanner key="pay-web" tone="critical" title="Платіжний backend недоступний">
        Сторінка оплати і перевірка LiqPay зараз не відповідають.
      </ActionBanner>,
    );
  }

  return banners;
}

function buildTelegramAccessState(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return {
        title: 'Доступ не надано',
        message: 'Цей Telegram акаунт не має доступу до Pako AdminApp.',
        detail: 'Доступ відкривається тільки за активним Telegram ID або після підтвердження номера через бота.',
      };
    }

    if (error.status === 409) {
      return {
        title: 'Потрібно перевідкрити Mini App',
        message: 'Telegram-підтвердження вже було використане. Відкрийте Mini App ще раз із бота.',
        detail: 'Це захист від повторного використання одного й того самого Telegram-підтвердження.',
      };
    }

    if (error.status === 503) {
      return {
        title: 'Mini App тимчасово недоступний',
        message: error.message || 'Telegram-підключення ще не налаштоване на сервері.',
        detail: 'Поки сервер не бачить підключення бота і сесію, Mini App не авторизує Telegram-вхід.',
      };
    }

    return {
      title: 'Вхід через Telegram не виконано',
      message: error.message || 'Не вдалося перевірити Telegram-підпис.',
      detail: 'Перевідкрийте Mini App із бота. Якщо проблема лишилась — перевірте підключення Telegram у налаштуваннях.',
    };
  }

  return {
    title: 'Вхід через Telegram не виконано',
    message: error instanceof Error ? error.message : 'Невідома помилка Telegram-авторизації.',
    detail: 'Перевідкрийте Mini App із бота й повторіть спробу.',
  };
}

function LoginScreen({
  loading,
  error,
  onSubmit,
  misconfigured,
}: {
  loading: boolean;
  error: string;
  misconfigured: string;
  onSubmit: (password: string) => Promise<void>;
}) {
  const [password, setPassword] = useState('');

  return (
    <div className="admin-login-screen">
      <div className="admin-login-card">
        <Card>
          <div className="admin-stack">
            <div className="admin-login-brand">
              <PakoLogo size={56} />
              <div className="admin-login-brand-copy">
                <Text as="h1" variant="headingXl">Pako AdminApp</Text>
                <Text as="p" variant="bodySm" tone="subdued">Web admin</Text>
              </div>
            </div>
            <Text as="p" variant="bodyMd" tone="subdued">
              Операційна панель для Shopify × LiqPay × 1С. Тут ми не редагуємо хаос, а керуємо дозволеним контуром.
            </Text>
            {misconfigured ? (
              <Banner tone="critical" title="Admin auth не налаштовано">
                <p>{misconfigured}</p>
              </Banner>
            ) : null}
            {error ? (
              <Banner tone="critical" title="Вхід не виконано">
                <p>{error}</p>
              </Banner>
            ) : null}
            <form
              className="admin-stack"
              onSubmit={async (event) => {
                event.preventDefault();
                await onSubmit(password);
              }}
            >
              <TextField
                autoComplete="current-password"
                label="Пароль"
                type="password"
                value={password}
                onChange={setPassword}
              />
              <Button submit variant="primary" loading={loading} disabled={Boolean(misconfigured)}>
                Увійти
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
}

function AppContent() {
  const [pathname, setPathname] = useState(resolvePathname(window.location.pathname));
  const [session, setSession] = useState<AdminSession | null>(null);
  const [overview, setOverview] = useState<any>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewRefreshError, setOverviewRefreshError] = useState('');
  const [overviewUpdatedAt, setOverviewUpdatedAt] = useState<string | null>(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [misconfiguredMessage, setMisconfiguredMessage] = useState('');
  const [telegramAccessState, setTelegramAccessState] = useState<null | {
    title: string;
    message: string;
    detail?: string;
  }>(null);
  const [telegramContext, setTelegramContext] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [toasts, setToasts] = useState<AdminToast[]>([]);

  const api = useMemo(() => createApiClient(() => session?.csrfToken || null), [session?.csrfToken]);
  const overviewPollIntervalMs = getOverviewPollIntervalMs(overview);

  const navigate = useCallback((nextPath: string) => {
    const normalized = resolvePathname(nextPath);
    window.history.pushState({}, '', normalized);
    setPathname(normalized);
  }, []);

  const loadOverview = useCallback(async (options: { quiet?: boolean } = {}) => {
    if (!session?.authenticated) return null;

    if (!options.quiet) {
      setOverviewLoading(true);
      setOverviewRefreshError('');
    }

    try {
      const nextOverview = await api.getOverview();
      setOverview(nextOverview);
      setOverviewUpdatedAt(new Date().toISOString());
      setOverviewRefreshError('');
      return nextOverview;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не вдалося оновити огляд';
      if (!options.quiet) {
        setOverviewRefreshError(message);
      }
      console.error(error);
      return null;
    } finally {
      if (!options.quiet) {
        setOverviewLoading(false);
      }
    }
  }, [api, session?.authenticated]);

  useEffect(() => {
    const handlePopState = () => setPathname(resolvePathname(window.location.pathname));
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setBootLoading(true);
      const telegramProbe = await waitForTelegramMiniApp();
      const initData = telegramProbe.initData || getTelegramInitData();
      const telegramLaunch = resolveTelegramLaunchState({
        initData,
        hinted: telegramProbe.hinted,
        webApp: telegramProbe.webApp,
      });
      if (cancelled) return;

      setTelegramContext(telegramLaunch.isTelegramContext);
      if (telegramLaunch.isTelegramContext) {
        prepareTelegramMiniApp();
      }

      try {
        const nextSession = await api.getSession();
        if (cancelled) return;

        if (nextSession.authenticated) {
          setSession(nextSession);
          setTelegramAccessState(null);
          setMisconfiguredMessage(nextSession.status === 'misconfigured' ? (nextSession.message || 'Admin auth не налаштовано') : '');
          return;
        }

        if (telegramLaunch.isTelegramContext && !telegramLaunch.shouldAutoAuth) {
          setSession({ authenticated: false });
          setTelegramAccessState({
            title: 'Немає Telegram-підтвердження',
            message: 'Mini App відкрився без службового підтвердження Telegram. Без нього доступ блокується.',
            detail: 'Відкрийте AdminApp кнопкою в боті, а не звичайним браузерним переходом.',
          });
          return;
        }

        const shouldUseTelegramAuth = telegramLaunch.shouldAutoAuth;

        if (shouldUseTelegramAuth) {
          setTelegramContext(true);
          try {
            const telegramSession = await authenticateTelegramWebappOnce(api, initData);
            if (cancelled) return;

            if (!telegramSession?.authenticated) {
              setSession({ authenticated: false });
              setTelegramAccessState({
                title: 'Доступ не підтверджено',
                message: 'Telegram перевірка не відкрила сесію AdminApp.',
                detail: 'Перевідкрийте Mini App із бота або перевірте правило доступу в Telegram Mini App налаштуваннях.',
              });
              return;
            }

            setSession(telegramSession);
            setTelegramAccessState(null);
            setMisconfiguredMessage('');
            return;
          } catch (error) {
            if (cancelled) return;
            setSession({ authenticated: false });
            setTelegramAccessState(buildTelegramAccessState(error));
            return;
          }
        }

        setSession(nextSession);
        setMisconfiguredMessage(nextSession.status === 'misconfigured' ? (nextSession.message || 'Admin auth не налаштовано') : '');
      } catch (error) {
        if (cancelled) return;
        const initData = getTelegramInitData();
        const launchState = resolveTelegramLaunchState({
          initData,
          hinted: telegramProbe.hinted,
          webApp: telegramProbe.webApp,
        });
        if (launchState.isTelegramContext) {
          setTelegramContext(true);
          setSession({ authenticated: false });
          setTelegramAccessState(buildTelegramAccessState(error));
          return;
        }
        if (error instanceof ApiError && error.status === 503) {
          setMisconfiguredMessage(error.message);
        }
        setSession({ authenticated: false });
      } finally {
        if (!cancelled) {
          setBootLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!session?.authenticated) return;

    void loadOverview({ quiet: true });
    const intervalId = window.setInterval(() => {
      void loadOverview({ quiet: true });
    }, overviewPollIntervalMs);
    return () => window.clearInterval(intervalId);
  }, [loadOverview, overviewPollIntervalMs, session?.authenticated]);

  const handleLogin = useCallback(async (password: string) => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const nextSession = await api.login(password);
      setSession(nextSession);
      setMisconfiguredMessage('');
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  }, [api, loadOverview]);

  const handleLogout = useCallback(async () => {
    await api.logout();
    setSession({ authenticated: false });
    setOverview(null);
    setOverviewLoading(false);
    setOverviewRefreshError('');
    setOverviewUpdatedAt(null);
    setLoginError('');
    navigate('/overview');
  }, [api, navigate]);

  const notify = useCallback((nextToast: Omit<AdminToast, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((current) => [...current.slice(-2), { ...nextToast, id }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 6500);
  }, []);

  if (bootLoading) {
    return (
      <div className="admin-login-screen">
        <div className="admin-login-card">
          <Card>
            <div className="admin-stack">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={4} />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!session?.authenticated) {
    if (telegramContext && telegramAccessState) {
      return (
        <TelegramAccessDenied
          title={telegramAccessState.title}
          message={telegramAccessState.message}
          detail={telegramAccessState.detail}
          onClose={closeTelegramMiniApp}
        />
      );
    }

    return (
      <LoginScreen
        loading={loginLoading}
        error={loginError}
        misconfigured={misconfiguredMessage}
        onSubmit={handleLogin}
      />
    );
  }

  const environment = presentTopbarEnvironment(overview);
  const readOnly = session.role === 'viewer';
  const commonPageProps = {
    api,
    overview,
    onRefreshOverview: () => loadOverview({ quiet: false }),
    overviewLoading,
    overviewRefreshError,
    overviewUpdatedAt,
    searchValue,
    notify,
    readOnly,
    sessionRole: session.role,
  };

  let page = null;
  if (pathname === '/overview') page = <OverviewPage {...commonPageProps} />;
  if (pathname === '/sync') page = <SyncPage {...commonPageProps} />;
  if (pathname === '/catalog') page = <CatalogPage {...commonPageProps} />;
  if (pathname === '/orders-payments') page = <OrdersPaymentsPage {...commonPageProps} />;
  if (pathname === '/nova-poshta-orders') page = <NovaPoshtaOrdersPage {...commonPageProps} />;
  if (pathname === '/settings') page = <SettingsPage {...commonPageProps} />;
  if (pathname === '/settings/telegram') page = <TelegramSettingsPage {...commonPageProps} />;
  if (pathname === '/settings/nova-poshta') page = <NovaPoshtaSettingsPage {...commonPageProps} />;
  if (pathname === '/logs') page = <LogsPage {...commonPageProps} />;

  return (
    <AdminShell
      currentPath={pathname}
      onNavigate={navigate}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      environmentLabel={environment.label}
      environmentStatus={environment.status}
      role={session.role}
      onLogout={handleLogout}
      banners={buildGlobalBanners(overview)}
      toasts={toasts}
      onToastDismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))}
    >
      {page}
    </AdminShell>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}
